import { getTdMapLocationCityName, getMapTilerReverseGeocode } from "@/utils/neturls";
import { CHINA_ALL_CITY_LIST, CHINA_ALL_PROVINCE, TD_MAP_KEY } from "@/utils/config";
import { IS_CN } from "@/effects/switchLanguageEffect";

/**
 * 根据经纬度获取城市名称（使用天地图接口）
 * @param {number} lat 纬度
 * @param {number} lng 经度
 * @returns {Promise<string | null>} 城市中文名，获取失败时返回 null
 */
export async function getCityByLatLng({ lat, lng }) {
  if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
    return null;
  }

  try {
    // 复用已有的天地图请求方法
    const res = await getTdMapLocationCityName({ lng, lat });
    const data = res?.data || res;
    console.log("getCityByLatLng", data);

    // 天地图返回结构参考：
    // {
    //   "status": "0",
    //   "msg": "ok",
    //   "result": {
    //     "addressComponent": {
    //       "province": "...",
    //       "city": "...",
    //       "district": "..."
    //     }
    //   }
    // }


    const status = data?.status ?? data?.data?.status;
    if (status !== "0" && status !== 0) {
      console.warn("getCityByLatLng: invalid status", status);
      return null;
    }

    const result = data?.result || data?.data?.result;
    const addressComponent =
      result?.addressComponent ||
      result?.address?.addressComponent ||
      null;

    if (!addressComponent) {
      return null;
    }

    // 优先返回城市，没有则退回省份
    const cityName =
      addressComponent.city ||
      addressComponent.cityName ||
      addressComponent.province ||
      null;

    return cityName || null;
  } catch (err) {
    console.error("getCityByLatLng (Tianditu): unexpected error", err);
    return null;
  }
}

/**
 * 根据经纬度获取匹配到的省份配置项
 * - 通过天地图逆解析拿到 province
 * - 再与 CHINA_ALL_PROVINCE 中的 text 进行匹配
 * - 匹配成功则返回对应的对象，匹配不到返回 null
 *
 * @param {{lat: number, lng: number}} param0
 * @returns {Promise<{text: string, value: string} | null>}
 */
export async function getProvinceConfigByLatLng({ lat, lng }) {
  // 改造：通过经纬度确定省份，并在 CHINA_ALL_PROVINCE 中匹配
  if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
    return null;
  }

  try {
    const res = await getTdMapLocationCityName({ lng, lat });
    const data = res?.data || res;

    const status = data?.status ?? data?.data?.status;
    if (status !== "0" && status !== 0) {
      return null;
    }

    const result = data?.result || data?.data?.result;
    const addressComponent =
      result?.addressComponent ||
      result?.address?.addressComponent ||
      null;

    const provinceName = addressComponent?.province || null;
    if (!provinceName) {
      return null;
    }

    const normalized = String(provinceName).replace(/\s+/g, "");
    const match = (CHINA_ALL_PROVINCE || []).find((item) => {

      if (!item || !item.text) return false;
      let textNorm;
      if(IS_CN()){
        textNorm =  String(item.text).replace(/\s+/g, "");
      }else{
        textNorm =  String(item.textcn).replace(/\s+/g, "");
      }
      return normalized.includes(textNorm) || textNorm.includes(normalized);
    });

    return match || null;
  } catch (e) {
    return null;
  }
}

/**
 * 根据经纬度获取匹配到的省份配置项（MapTiler 逆地理编码）
 */
export async function getProvinceConfigByLatLngMapTiler({
  lat,
  lng,
  provinceList,
  geocodeLanguage = "zh",
}) {
  if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
    return null;
  }

  try {
    const res = await getMapTilerReverseGeocode({
      lng,
      lat,
      language: geocodeLanguage,
    });
    const data = res?.data || res;
    const features = data?.features;
    if (!Array.isArray(features) || features.length === 0) {
      return null;
    }

    const context = features[0]?.context || [];
    let provinceName = null;
    for (const ctx of context) {
      const id = String(ctx?.id || "");
      if (
        id.startsWith("region.") ||
        id.startsWith("province.") ||
        id.startsWith("state.")
      ) {
        provinceName = ctx.text;
        break;
      }
    }
    if (!provinceName) {
      provinceName = features[0]?.text || null;
    }
    if (!provinceName) {
      return null;
    }

    const normalized = String(provinceName).replace(/\s+/g, "").toLowerCase();
    const list = provinceList || CHINA_ALL_PROVINCE || [];
    const match = list.find((item) => {
      if (!item || !item.text) return false;
      const textNorm = String(item.text).replace(/\s+/g, "").toLowerCase();
      const textCnNorm = String(item.textcn || item.text)
        .replace(/\s+/g, "")
        .toLowerCase();
      return (
        normalized.includes(textNorm) ||
        textNorm.includes(normalized) ||
        normalized.includes(textCnNorm) ||
        textCnNorm.includes(normalized)
      );
    });

    return match || null;
  } catch (e) {
    return null;
  }
}

/**
 * 通过两组经纬度计算大概距离（单位：km）
 * 使用 Haversine 公式，适合做“直线距离”的粗略估算。
 *
 * @param {{lat: number, lng: number}} from
 * @param {{lat: number, lng: number}} to
 * @returns {number} 距离（km），参数不合法时返回 0
 */
export function calcDistanceKm(from, to) {
  const lat1 = Number(from?.lat);
  const lng1 = Number(from?.lng);
  const lat2 = Number(to?.lat);
  const lng2 = Number(to?.lng);

  if ([lat1, lng1, lat2, lng2].some((v) => Number.isNaN(v))) {
    return 0;
  }

  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371; // 地球平均半径（km）

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) *
    Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

/**
 * 使用天地图 JS API 计算“驾车路线总里程”（单位：km，取整）
 * - 需要页面已加载天地图 JS（window.T）
 * - 需要传入一个天地图 Map 实例（T.Map），用于 DrivingRoute 的构造
 * - 仅取路线总里程，不做路线绘制
 *
 * @param {{lat:number,lng:number}} from
 * @param {{lat:number,lng:number}} to
 * @param {any} tdMapInstance 天地图 map 实例（T.Map）
 * @param {{timeoutMs?: number}} [options]
 * @returns {Promise<number>} 路线里程（km，整数）；失败时返回 0
 */
export function calcTdDrivingDistanceKm(from, to, tdMapInstance, options = {}) {
  const timeoutMs = Number(options?.timeoutMs ?? 8000);
  const lat1 = Number(from?.lat);
  const lng1 = Number(from?.lng);
  const lat2 = Number(to?.lat);
  const lng2 = Number(to?.lng);

  if ([lat1, lng1, lat2, lng2].some((v) => Number.isNaN(v))) {
    console.log("calcTdDrivingDistanceKm no tdMapInstanceone errror");
    return Promise.resolve(0);
  }

  // 没有天地图环境或没有 map 实例时，退化为直线距离（避免页面其它地方距离都变 0）
  if (typeof window === "undefined" || !window.T || !tdMapInstance) {
    console.log("calcTdDrivingDistanceKm no tdMapInstanceone", window.T);
    console.log("calcTdDrivingDistanceKm no tdMapInstanceone", !window.T);
    console.log("calcTdDrivingDistanceKm no tdMapInstanceone", tdMapInstance);
    console.log("calcTdDrivingDistanceKm no tdMapInstanceone", !tdMapInstance);


    console.log("calcTdDrivingDistanceKm no tdMapInstance");
    return Promise.resolve(calcDistanceKm(from, to));
  }

  const T = window.T;

  const extractDistanceMeters = (result) => {
    // 兼容不同版本/不同返回结构，尽量从多个路径取值
    try {
      // 常见：result.getPlan(0).getDistance() / getDistance() 返回米
      if (result?.getPlan) {
        const plan0 = result.getPlan(0);
        const d =
          plan0?.getDistance?.() ??
          plan0?.distance ??
          plan0?.getDistance ??
          null;
        if (typeof d === "number") return d;
        const dn = Number(d);
        if (Number.isFinite(dn)) return dn;
      }

      const cand =
        result?.distance ??
        result?.Distance ??
        result?.route?.distance ??
        result?.routes?.[0]?.distance ??
        result?.result?.distance ??
        result?.result?.routes?.[0]?.distance ??
        null;
      const dn = Number(cand);
      if (Number.isFinite(dn)) return dn;
    } catch (e) { }
    return null;
  };

  return new Promise((resolve) => {
    let done = false;
    const finish = (km) => {
      if (done) return;
      done = true;
      resolve(Number.isFinite(km) ? km : 0);
    };

    const timer = setTimeout(() => finish(0), timeoutMs);

    try {
      const start = new T.LngLat(lng1, lat1);
      const end = new T.LngLat(lng2, lat2);

      const driving = new T.DrivingRoute(tdMapInstance, {
        onSearchComplete: (result) => {
          clearTimeout(timer);
          const meters = extractDistanceMeters(result);
          if (!meters || !Number.isFinite(meters)) {
            finish(0);
            return;
          }
          finish(Math.round(meters / 1000));
        },
      });

      driving.search(start, end);
    } catch (e) {
      clearTimeout(timer);
      finish(0);
    }
  });
}

/**
 * 使用天地图“驾车路线规划”服务端接口，计算路线总里程（单位：km，取整）
 * 接口形态（示例）：
 * https://api.tianditu.gov.cn/drive?postStr={"orig":"lng,lat","dest":"lng,lat","style":"0"}&type=search&tk=xxx
 *
 * 说明：
 * - 返回格式在不同版本/网关下可能为 JSON 或 XML，本方法做了兼容解析
 * - 仅用于获取距离，不绘制路线
 *
 * @param {{lat:number,lng:number}} from
 * @param {{lat:number,lng:number}} to
 * @param {{style?: string|number, timeoutMs?: number, tk?: string}} [options]
 * @returns {Promise<number>} 路线里程（km，整数）；失败时返回 0
 */
export async function calcTdDrivingDistanceKmByApi(from, to, options = {}) {
  const lat1 = Number(from?.lat);
  const lng1 = Number(from?.lng);
  const lat2 = Number(to?.lat);
  const lng2 = Number(to?.lng);
  if ([lat1, lng1, lat2, lng2].some((v) => Number.isNaN(v))) return 0;

  const tk = String(options?.tk || TD_MAP_KEY || "").trim();
  if (!tk) return 0;

  const style = options?.style != null ? String(options.style) : "0";
  const timeoutMs = Number(options?.timeoutMs ?? 8000);

  const postStrObj = {
    orig: `${lng1},${lat1}`,
    dest: `${lng2},${lat2}`,
    style,
  };

  const url = new URL("https://api.tianditu.gov.cn/drive");
  url.searchParams.set("postStr", JSON.stringify(postStrObj));
  url.searchParams.set("type", "search");
  url.searchParams.set("tk", tk);

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  const extractDistanceMetersFromJson = (obj) => {
    if (!obj || typeof obj !== "object") return null;

    const candidates = [
      obj?.distance,
      obj?.Distance,
      obj?.route?.distance,
      obj?.routes?.[0]?.distance,
      obj?.result?.distance,
      obj?.result?.route?.distance,
      obj?.result?.routes?.[0]?.distance,
    ];
    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n) && n > 0) return n;
    }

    for (const [k, v] of Object.entries(obj)) {
      if (k.toLowerCase() === "distance") {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return n;
      }
      if (v && typeof v === "object") {
        const found = extractDistanceMetersFromJson(v);
        if (found) return found;
      }
    }
    return null;
  };

  const extractDistanceMetersFromXmlText = (text) => {
    try {
      if (typeof DOMParser !== "undefined") {
        const doc = new DOMParser().parseFromString(text, "text/xml");
        const el =
          doc.querySelector("distance") ||
          doc.querySelector("Distance") ||
          doc.querySelector("distancevalue");
        const raw = el?.textContent;
        const n = Number(raw);
        if (Number.isFinite(n) && n > 0) return n;
      }
    } catch (e) {}

    const m = text.match(/<distance>\s*([0-9.]+)\s*<\/distance>/i);
    if (m && m[1]) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const m2 = text.match(/"distance"\s*:\s*"?(?<d>[0-9.]+)"?/i);
    if (m2?.groups?.d) {
      const n = Number(m2.groups.d);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  try {
    const res = await fetch(url.toString(), {
      method: "GET",
      signal: controller?.signal,
    });
    const text = await res.text();

    try {
      const json = JSON.parse(text);
      const meters = extractDistanceMetersFromJson(json);
      if (meters && Number.isFinite(meters)) return Math.round(meters / 1000);
    } catch (e) {
      // ignore
    }

    const meters = extractDistanceMetersFromXmlText(text);
    if (meters && Number.isFinite(meters)) return Math.round(meters / 1000);
    return 0;
  } catch (e) {
    return 0;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * 批量计算多终点的驾车路线里程（km，整数）
 * - 通过并发限速 + 缓存，显著减少“该tk已限流”的概率
 * - 被限流/失败时，自动回退到直线距离（保证列表可用）
 *
 * @param {{lat:number,lng:number}} from
 * @param {Array<{lat:number,lng:number}>} toList
 * @param {{concurrency?: number, tk?: string, style?: string|number, timeoutMs?: number}} [options]
 * @returns {Promise<number[]>} 与 toList 等长的距离数组（km 整数）
 */
export async function calcTdDrivingDistanceKmByApiBatch(from, toList, options = {}) {
  const concurrency = Math.max(1, Number(options?.concurrency ?? 1));
  const list = Array.isArray(toList) ? toList : [];
  if (list.length === 0) return [];

  const round6 = (n) => Math.round(Number(n) * 1e6) / 1e6;
  const cacheKey = (a, b) =>
    `${round6(a?.lng)},${round6(a?.lat)}->${round6(b?.lng)},${round6(b?.lat)}`;

  // 简单内存缓存（本次页面生命周期内复用）
  if (!calcTdDrivingDistanceKmByApiBatch.__cache) {
    calcTdDrivingDistanceKmByApiBatch.__cache = new Map();
  }
  const cache = calcTdDrivingDistanceKmByApiBatch.__cache;

  const results = new Array(list.length).fill(0);
  let cursor = 0;

  const worker = async () => {
    while (cursor < list.length) {
      const idx = cursor++;
      const to = list[idx];
      const key = cacheKey(from, to);

      if (cache.has(key)) {
        results[idx] = cache.get(key);
        continue;
      }

      // 计算一次；失败/限流就回退直线距离
      let km = 0;
      try {
        km = await calcTdDrivingDistanceKmByApi(from, to, options);
        if (!km) km = calcDistanceKm(from, to);
      } catch (e) {
        km = calcDistanceKm(from, to);
      }

      cache.set(key, km);
      results[idx] = km;
    }
  };

  const workers = [];
  for (let i = 0; i < Math.min(concurrency, list.length); i++) {
    workers.push(worker());
  }
  await Promise.all(workers);
  return results;
}


